// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { Button, Typography } from "@insite/mobius/index";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { formatDateTime, styledScrollbar, xScreen } from "@stanion/XDL/Utilities";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomAppState) => ({
    billToDataView: getCurrentBillToState(state).value,
    customSettings: getSettingsCollection(state).customSettings,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    loadCurrentBillTo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface StatementSummaryStyles {
    container?: InjectableCss;
    item?: InjectableCss;
    messageStyledWrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
    wrapper?: InjectableCss;
    text?: SmallHeadingAndTextStyles;
}

export const statementSummaryStyles: StatementSummaryStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-bottom: 20px;
        `,
    },
    item: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: nowrap;
            width: 100%;
            column-gap: 10px;
            padding-bottom: 15px;
            margin-bottom: 15px;
            overflow-x: auto;
            position: relative;
            ${styledScrollbar()};
            ${xScreen("lte", "sm")} {
                width: auto;
            }
        `,
    },
    messageStyledWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 10px;
        `,
    },
    buttonContainer: {
        css: css`
            min-width: 225px;
            margin-left: 10px;
            width: auto;
        `,
    },
    text: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 100%;
                min-width: 145px;
                @media print {
                    display: block;
                }
            `,
        },
        text: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
};

const styles = statementSummaryStyles;

const InvoiceHistoryStatementSummary = (props: Props) => {
    React.useEffect(() => {
        if (props.billToDataView) {
            props.loadCurrentBillTo();
        }
    }, []);

    const payOnlineButton = () => {
        if (!props.history) {
            return;
        }
        window.location.href = (props.customSettings as any).billTrustUrl;
    };

    const { billToDataView } = props;
    if (!billToDataView) {
        return null;
    }

    const { properties } = billToDataView;
    const data = [
        {
            amountDisplay: properties.lastPaymentDate
                ? `${getLocalizedDateTime({
                      dateTime: new Date(properties?.lastPaymentDate),
                      language: props.language,
                      options: {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                      },
                  })} - ${formatDateTime(new Date(properties?.lastPaymentDate))}`
                : "--",
            label: translate("Last Payment Date"),
        },
        {
            amountDisplay: properties?.highBalance ? `$ ${properties?.highBalance}` : "--",
            label: translate("High Balance"),
        },
        {
            amountDisplay: properties?.openOrderBalance ? `$ ${properties?.openOrderBalance}` : "--",
            label: translate("Open Order Balance"),
        },
        {
            amountDisplay: properties?.futureOrderBalance ? `$ ${properties?.futureOrderBalance}` : "--",
            label: translate("Future Invoice Balance"),
        },
        {
            amountDisplay: properties?.lastPaymentAmount ? `$ ${properties?.lastPaymentAmount}` : "--",
            label: translate("Last Payment Amount"),
        },
    ];

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="invoiceHistory_StatmentSummary">
            <StyledWrapper {...styles.item}>
                {data.map((agingBucket, index) => (
                    <SmallHeadingAndText
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        heading={agingBucket.label}
                        text={agingBucket.amountDisplay}
                        extendedStyles={styles.text}
                    />
                ))}
            </StyledWrapper>
            <StyledWrapper {...styles.messageStyledWrapper}>
                <Button onClick={() => payOnlineButton()}>{translate("Pay Online - Invoice Gateway")}</Button>
            </StyledWrapper>
            <StyledWrapper {...styles.messageStyledWrapper}>
                <Typography>{siteMessage("Custom_InvoiceHistory_PaymentQuestions")}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(InvoiceHistoryStatementSummary)),
    definition: {
        group: "Invoice History",
        displayName: "Statement Summary",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
